/* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — weight-designer-kb.js
 * ==========================================================================
 * Progressive enhancement for the WD Help & Guide KB. The Liquid ships fully
 * expanded, readable, crawlable markup; this file adds search, the category
 * rail, accordions, and PAGING on top. Rebuilt from the GLD KB
 * (custom-designer-kb.js) with its known flaws fixed:
 *   • tokenized AND search with field scoring + <mark> highlighting + a live
 *     result count (the GLD is a single indexOf over one concatenated string)
 *   • content visible by DEFAULT ("All" active; the GLD lands on nothing)
 *   • clicking the active tab does NOT deselect; clearing search restores the
 *     previous tab (the GLD resets to the empty state)
 *   • roving tabindex on the tablist; no dangling aria-controls; no
 *     unconditional navigator.vibrate
 *   • multi-instance safe: every .wd-kb root initializes independently
 *
 * ── PAGING (2026-08-13) ───────────────────────────────────────────────────
 * Ryan: *"How big it is. When you click on a pill like 'All', the questions go
 * down 4 pages long."* — 114 collapsed rows is about seven screens. So the list
 * shows TEN AT A TIME behind a pager, searches included, and the frame holds one
 * height whichever category, page or query is up.
 *
 * ⛔ THE HEIGHT IS THE FEATURE, and it is easy to break. Three separate things
 * moved the Next button before this settled, each found by walking every page of
 * every category at three viewports rather than by looking at one:
 *   1. category headings (33px each, and a page carries a varying NUMBER of them)
 *   2. the pager disappearing on a single-page category (76px)
 *   3. a question wrapping to two lines (16px, and 81 of 114 wrap at 390px)
 * Anything added here that can change a row's or the frame's height has to be
 * measured across all of them, not eyeballed on the default category.
 *
 * ⛔ NONE OF IT TOUCHES THE CONTENT. Every answer stays in the initial HTML on
 * one URL, the FAQPage JSON-LD in the snippet is untouched and still lists all
 * of them, and paging is a class over markup that is already there — the same
 * mechanism that already hid eight of the nine category panels. <noscript> still
 * re-expands everything, so a JS-off crawler sees the whole corpus with no pager.
 * ========================================================================== */
(function () {
  'use strict';

  /* Ryan 2026-08-14: ten to a page on DESKTOP, five on a PHONE — ten was unwieldy on a phone but
     right on the wide column. So it is per-breakpoint, not a constant.
     ⚠ 900 is the stylesheet's own boundary — `@media (min-width: 900px)` is where .wd-kb__body
     becomes the two-column grid and the rail stops being a horizontal tab row. Paging on a
     different number than the layout switches on would give a phone-shaped list a desktop page
     count somewhere in between.
     ⚠ Read through the function EVERY time rather than cached: it is both the row cap (pagesOf)
     and the height budget (measureFloor), and a value captured at init would survive a rotate. */
  function perPage() {
    return window.innerWidth >= 900 ? 10 : 5;
  }

  function init(kb) {
    if (kb.dataset.wdKbInit) return;
    kb.dataset.wdKbInit = '1';
    kb.classList.add('wd-kb--js');

    var searchWrap = kb.querySelector('.wd-kb__search');
    var input = kb.querySelector('.wd-kb__search-input');
    var clearBtn = kb.querySelector('.wd-kb__search-clear');
    var countEl = kb.querySelector('.wd-kb__result-count');
    var noResults = kb.querySelector('.wd-kb__no-results');
    var askBtn = kb.querySelector('.wd-kb__ask');
    var tablist = kb.querySelector('.wd-kb__tabs');
    var viewport = kb.querySelector('.wd-kb__viewport');
    var stack = kb.querySelector('.wd-kb__stack');
    var pager = kb.querySelector('.wd-kb__pager');
    var prevBtn = kb.querySelector('.wd-kb__pager-btn[data-page="prev"]');
    var nextBtn = kb.querySelector('.wd-kb__pager-btn[data-page="next"]');
    var atEl = kb.querySelector('.wd-kb__pager-at');
    var tabs = Array.prototype.slice.call(kb.querySelectorAll('.wd-kb__tab'));
    var panels = Array.prototype.slice.call(kb.querySelectorAll('.wd-kb__category'));
    var items = Array.prototype.slice.call(kb.querySelectorAll('.wd-kb__item'));
    if (!tablist || !panels.length || !viewport || !stack) return;

    // ── build the search index once: per item, the fields it can match on ──
    var index = items.map(function (item, i) {
      var qEl = item.querySelector('.wd-kb__q-text');
      var aEl = item.querySelector('.wd-kb__a-inner');
      var panel = item.closest('.wd-kb__category');
      return {
        i: i,                    // position in `items`, and so into the row-height tables
        item: item,
        panel: panel,
        cat: panel ? panel.getAttribute('data-category') : '',
        q: (qEl ? qEl.textContent : '').toLowerCase(),
        tags: (item.getAttribute('data-tags') || '').toLowerCase(),
        body: (aEl ? aEl.textContent : '').toLowerCase(),
        qEl: qEl,
        qOriginal: qEl ? qEl.textContent : ''
      };
    });

    // ── accordions ─────────────────────────────────────────────────────────
    // Items are ALREADY collapsed by CSS at parse time (see the note in
    // weight-designer-kb.css) — this only supplies the ARIA that markup can't
    // carry without lying to no-JS users.
    items.forEach(function (item) {
      var btn = item.querySelector('.wd-kb__q-btn');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
    kb.addEventListener('click', function (ev) {
      var btn = ev.target.closest('.wd-kb__q-btn');
      if (!btn || !kb.contains(btn)) return;
      toggleItem(btn.closest('.wd-kb__item'));
    });
    function toggleItem(item, forceOpen) {
      if (!item) return;
      var btn = item.querySelector('.wd-kb__q-btn');
      var open = item.classList.contains('is-open');
      var next = forceOpen === true ? true : !open;
      item.classList.toggle('is-open', next);
      if (btn) btn.setAttribute('aria-expanded', next ? 'true' : 'false');
      // Glyph swap, same as the product-page Custom Q&A block: "+" ↔ "−" (U+2212
      // minus, not a hyphen — it matches the plus's width and weight). The 360°
      // spin that carries it is the CSS transition on .wd-kb__q-icon.
      var icon = item.querySelector('.wd-kb__q-icon');
      if (icon) icon.textContent = next ? '−' : '+';
    }
    function closeAll() {
      items.forEach(function (item) {
        item.classList.remove('is-open');
        var btn = item.querySelector('.wd-kb__q-btn');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        var icon = item.querySelector('.wd-kb__q-icon');
        if (icon) icon.textContent = '+';
      });
    }

    // ── the floor, and how a page is filled ────────────────────────────────
    // ⛔ A PAGE IS FILLED BY HEIGHT, NOT BY COUNT, and the two are not the same
    // question once questions wrap. Rows come in three heights at 390px (one,
    // two and three lines) and 88 of the 114 questions are not one-liners, so
    // "ten rows" is anywhere from 580px to 900px of list. A frame tall enough
    // for the worst ten therefore left a typical page sitting in ~380px of empty
    // navy — constant height, achieved by wasting most of it.
    //   So the frame is a BUDGET — PER_PAGE rows of the shortest kind — and each
    // page takes as many rows as fit inside it, up to PER_PAGE. Every page ends
    // within one row of the budget instead of within one row of the worst case,
    // no page can ever exceed it (which is what keeps the height fixed), and the
    // only short page is the last one of a category, which is what a paginated
    // list is supposed to look like.
    // 8px is the gap .wd-kb__item's margin-bottom supplies.
    var rowH = [];        // per item, in DOM order — single-category view
    var rowHMulti = [];   // …and with the in-row category label, which can wrap it
    var budget = 0;

    function measureSet(multi) {
      stack.classList.add('is-measuring');
      if (multi) stack.classList.add('is-measuring-multi');
      var hs = items.map(function (item) { return item.getBoundingClientRect().height; });
      stack.classList.remove('is-measuring', 'is-measuring-multi');
      return hs;
    }
    /* ⛔ THE PHONE IS COUNT-FIRST, THE DESKTOP IS HEIGHT-FIRST, and that is Ryan's call rather than
       an inconsistency. On the wide column almost every row is one line, so a budget of ten SHORTEST
       rows fits ten actual rows and the frame stays tight. On a phone most rows wrap to two or three
       lines, so a budget of five shortest rows fits FOUR — which is what he saw, twice. He asked for
       five, so on a phone the count wins and the frame is sized to the worst run of five instead.
       The cost is real and is the one the header above warns about: pages of short rows leave empty
       navy at the bottom. That is the price of a guaranteed count with a fixed frame, and a fixed
       frame is not optional — the page-turn clips against it. */
    function countFirst() { return perPage() < 10; }

    function measureFloor() {
      if (!items.length) return;
      rowH = measureSet(false);
      rowHMulti = measureSet(true);
      var pp = perPage();
      if (countFirst()) {
        /* The tallest RUN of pp consecutive rows, not pp x the tallest row — the latter assumes the
           worst row repeats pp times, which no category does, and would pad the frame by hundreds of
           px. Both height tables are considered because the "All" view carries the category label,
           which can wrap a row that does not wrap inside its own category. */
        var worst = 0;
        [rowH, rowHMulti].forEach(function (tbl) {
          for (var s = 0; s < tbl.length; s++) {
            var sum = 0;
            for (var k = 0; k < pp && s + k < tbl.length; k++) sum += (tbl[s + k] || 0) + (k ? 8 : 0);
            if (sum > worst) worst = sum;
          }
        });
        if (!isFinite(worst) || worst <= 0) return;
        budget = Math.ceil(worst);
      } else {
        var shortest = Math.min.apply(null, rowH.filter(function (h) { return h > 0; }));
        if (!isFinite(shortest) || shortest <= 0) return;
        budget = Math.ceil(shortest * pp + 8 * (pp - 1));
      }
      kb.style.setProperty('--wd-kb-min-h', budget + 'px');
      measureRailTail();
    }

    /* How much travel to take OFF the sticky rail so it stops level with the bottom of the last
       question instead of against the pager. See the ⛔ on .wd-kb__tabs' margin in the stylesheet:
       Chrome constrains a sticky grid item to the grid CONTAINER, so the row placement alone does
       not bound it and the rail's own margin has to. Measured rather than hardcoded because the
       number is the pager's border + padding + button metrics, which a restyle would move. */
    function measureRailTail() {
      if (!pager) return;
      var cs = window.getComputedStyle(pager);
      var tail = cs.display === 'none'
        ? 0
        : pager.getBoundingClientRect().height + (parseFloat(cs.marginTop) || 0);
      kb.style.setProperty('--wd-kb-rail-tail', Math.max(0, Math.round(tail)) + 'px');
    }

    // Greedy fill, in scope order. Returns the page boundaries as an array of
    // [startIndex, count]. Always places at least one row, so a row taller than
    // the whole budget could never produce an empty page and an infinite loop.
    // (Still impossible at the phone's five: the budget is five of the shortest
    // plus four gaps, and the tallest row is a three-liner — well under it.)
    function pagesOf(scope, multi) {
      var heights = multi ? rowHMulti : rowH;
      var cap = perPage();
      var out = [];
      var i = 0;
      while (i < scope.length) {
        var h = 0, n = 0;
        while (i + n < scope.length && n < cap) {
          var rh = (heights[scope[i + n].i] || 0) + (n ? 8 : 0);
          // Count-first (phone): the cap alone ends the page — the frame was measured to hold the
          // worst run of `cap`, so no page can overflow it and the height check would only ever
          // cut a page short of the count Ryan asked for.
          if (!countFirst() && n > 0 && budget > 0 && h + rh > budget) break;
          h += rh; n++;
        }
        if (n === 0) n = 1;
        out.push([i, n]);
        i += n;
      }
      return out.length ? out : [[0, 0]];
    }

    // ── tabs ───────────────────────────────────────────────────────────────
    // The resting default is the FIRST CATEGORY, not "All" (Ryan 2026-08-01).
    // "All" lists 100+ question titles at once, which reads as a phone book;
    // one category is a screenful you can actually take in, and the rail makes
    // the rest one click away. Search still spans everything regardless, and
    // crawlers are unaffected (panel filtering is a JS-only class).
    var firstCategoryTab = tabs.filter(function (t) { return t.getAttribute('data-category') !== 'all'; })[0];
    var DEFAULT_CATEGORY = firstCategoryTab ? firstCategoryTab.getAttribute('data-category') : 'all';
    var activeCategory = DEFAULT_CATEGORY;
    var preSearchCategory = DEFAULT_CATEGORY;
    var page = 0;
    var turning = false;

    function activate(category, keepPage) {
      activeCategory = category;
      if (!keepPage) page = 0;
      tabs.forEach(function (tab) {
        var on = tab.getAttribute('data-category') === category;
        tab.classList.toggle('wd-kb__tab--active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      applyFilters();
    }
    tablist.addEventListener('click', function (ev) {
      var tab = ev.target.closest('.wd-kb__tab');
      if (!tab) return;
      if (input && input.value) { input.value = ''; currentTokens = []; syncClear(); } // a tab click always wins over a stale query
      closeAll();
      activate(tab.getAttribute('data-category'));
    });
    tablist.addEventListener('keydown', function (ev) {
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var next = null;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') next = (i + 1) % tabs.length;
      else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
      else if (ev.key === 'Home') next = 0;
      else if (ev.key === 'End') next = tabs.length - 1;
      if (next === null) return;
      ev.preventDefault();
      tabs[next].focus();
      closeAll();
      activate(tabs[next].getAttribute('data-category'));
    });

    // ── search: tokenized AND across question/tags/body, scored, highlighted ─
    function tokenize(raw) {
      return raw.toLowerCase().split(/[^a-z0-9.+]+/).filter(function (t) { return t.length > 1; });
    }
    function scoreEntry(entry, tokens) {
      var score = 0;
      for (var i = 0; i < tokens.length; i++) {
        var t = tokens[i];
        var inQ = entry.q.indexOf(t) !== -1;
        var inTags = entry.tags.indexOf(t) !== -1;
        var inBody = entry.body.indexOf(t) !== -1;
        if (!inQ && !inTags && !inBody) return 0; // AND semantics: every token must land somewhere
        score += (inQ ? 3 : 0) + (inTags ? 2 : 0) + (inBody ? 1 : 0);
      }
      return score;
    }
    function highlight(entry, tokens) {
      if (!entry.qEl) return;
      if (!tokens.length) { entry.qEl.textContent = entry.qOriginal; return; }
      var pattern = tokens.map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|');
      var re = new RegExp('(' + pattern + ')', 'gi');
      var html = '';
      entry.qOriginal.split(re).forEach(function (part) {
        var escaped = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        html += re.test(part) ? '<mark>' + escaped + '</mark>' : escaped;
        re.lastIndex = 0;
      });
      entry.qEl.innerHTML = html;
    }

    var currentTokens = [];

    // Which items are in scope at all, in DOM order (which is corpus order).
    function scopeOf() {
      return index.filter(function (entry) {
        if (currentTokens.length) return scoreEntry(entry, currentTokens) > 0;
        return activeCategory === 'all' || entry.cat === activeCategory;
      });
    }
    function isMulti() { return currentTokens.length > 0 || activeCategory === 'all'; }

    function applyFilters() {
      var searching = currentTokens.length > 0;
      var scope = scopeOf();
      var breaks = pagesOf(scope, isMulti());
      var pages = breaks.length;
      if (page > pages - 1) page = pages - 1;
      if (page < 0) page = 0;

      var onPage = scope.slice(breaks[page][0], breaks[page][0] + breaks[page][1]);
      var visible = onPage.map(function (e) { return e.item; });

      index.forEach(function (entry) {
        var vis = visible.indexOf(entry.item) !== -1;
        entry.item.classList.toggle('wd-kb__item--hidden', !vis);
        highlight(entry, searching && vis ? currentTokens : []);
      });
      panels.forEach(function (panel) {
        var any = panel.querySelector('.wd-kb__item:not(.wd-kb__item--hidden)');
        panel.classList.toggle('wd-kb__category--active', !!any);
      });

      // The in-row category label earns its place only when the list spans more
      // than one category — anywhere else the rail already answers it.
      kb.classList.toggle('wd-kb--multi', searching || activeCategory === 'all');
      // An empty search is the one place the floor should NOT hold: there is
      // nothing to page, so a frame-height box of empty navy above the
      // "no answers" card would be all cost and no benefit.
      kb.classList.toggle('wd-kb--empty', scope.length === 0);

      if (prevBtn) prevBtn.disabled = page === 0;
      if (nextBtn) nextBtn.disabled = page === pages - 1;
      /* The word "Page" is its own element so a phone can drop it (see the CSS). At 375px the
         pager is arrows + label on one row and "Page 1 of 2" wrapped mid-label; "1 of 2" says the
         same thing in the space available. Desktop keeps the full wording — it has the room and
         reads better (Ryan 2026-08-13). */
      if (atEl) atEl.innerHTML = '<span class="wd-kb__pager-word">Page </span><b>' + (page + 1) + '</b> of ' + pages;

      if (noResults) noResults.hidden = !searching || scope.length > 0;
      if (countEl) {
        // ⛔ EMPTY THE TEXT, NEVER RE-HIDE IT — the element keeps its line (see
        // the min-height in the stylesheet). Toggling `hidden` here made every
        // search state 26px taller than every browse state.
        // The TRUE total, not the page's — the count answers "did it find it",
        // and reporting 10 when 40 matched would read as a failed search.
        countEl.textContent = !searching ? ''
          : (scope.length === 1 ? '1 answer matches' : scope.length + ' answers match');
      }
      // While searching, auto-open lone strong hits so the answer is right there.
      if (searching && scope.length > 0 && scope.length <= 3) {
        onPage.forEach(function (entry) { toggleItem(entry.item, true); });
      }
    }

    // ── the page turn ──────────────────────────────────────────────────────
    // The whole page moves as ONE block: out of the frame upward, swap while
    // invisible, then up from below into place. Never row by row — a stagger
    // across ten rows reads as a carousel shuffling cards, not a page.
    var OUT = 200, GAP = 30, IN = 280;
    function reduced() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    /* Turning a page rebuilds the list ABOVE the button that was just clicked,
       so without this the visitor can be left staring at the pager with the new
       page off the top of the screen. Only acts when the list top is genuinely
       above the fold — at ten rows it usually is not, and a scroll nobody asked
       for is worse than none. Calls back when the scroll has settled, so the
       turn never races it. */
    function ensureListTop(done) {
      var top = viewport.getBoundingClientRect().top + window.pageYOffset - 90;
      if (window.pageYOffset <= top + 4) return done();
      if (reduced() || !('scrollBehavior' in document.documentElement.style)) {
        window.scrollTo(0, top);
        return done();
      }
      window.scrollTo({ top: top, behavior: 'smooth' });
      var last = -1, still = 0;
      (function watch() {
        if (window.pageYOffset === last && ++still > 2) return done();
        if (window.pageYOffset !== last) { last = window.pageYOffset; still = 0; }
        requestAnimationFrame(watch);
      })();
    }
    function turn(to) {
      if (turning) return;
      if (reduced()) { page = to; closeAll(); applyFilters(); ensureListTop(function () {}); return; }
      turning = true;
      ensureListTop(function () {
        // The frame's height, not the stack's: a short page is shorter than the
        // frame it sits in, and translating by its own 100% would leave it
        // mid-frame instead of below it.
        var frame = viewport.getBoundingClientRect().height;
        kb.style.setProperty('--wd-kb-turn-d', Math.ceil(frame) + 'px');
        viewport.style.height = frame + 'px';
        viewport.classList.add('is-turning');
        stack.classList.add('is-out');

        setTimeout(function () {
          // Invisible from here to the reflow below — the swap is never seen.
          stack.classList.add('no-anim');
          page = to;
          closeAll();
          applyFilters();
          stack.classList.remove('is-out');
          stack.classList.add('is-below');
          void stack.offsetWidth;                       // commit the jump untweened
          stack.classList.remove('no-anim');
          stack.classList.add('is-entering');
          viewport.style.height = stack.getBoundingClientRect().height + 'px';
          requestAnimationFrame(function () { stack.classList.remove('is-below'); });

          setTimeout(function () {
            stack.classList.remove('is-entering');
            viewport.classList.remove('is-turning');
            viewport.style.height = '';
            turning = false;
          }, IN + 40);
        }, OUT + GAP);
      });
    }
    if (prevBtn) prevBtn.addEventListener('click', function () { if (!prevBtn.disabled) turn(page - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { if (!nextBtn.disabled) turn(page + 1); });

    var debounce = null;
    function onQuery() {
      var raw = input.value.trim();
      var wasSearching = currentTokens.length > 0;
      currentTokens = tokenize(raw);
      page = 0;
      closeAll();
      if (currentTokens.length && !wasSearching) preSearchCategory = activeCategory;
      if (!currentTokens.length && wasSearching) { activate(preSearchCategory); syncClear(); return; }
      syncClear();
      applyFilters();
    }
    function syncClear() { if (clearBtn) clearBtn.hidden = !(input && input.value); }
    if (input) {
      searchWrap.hidden = false;
      // Un-hidden ONCE, here rather than per-render: from now on it always holds
      // its line and only its text changes. It stays hidden in the markup for the
      // JS-off visitor, who has no search box to produce a count for.
      if (countEl) countEl.hidden = false;
      input.addEventListener('input', function () {
        clearTimeout(debounce);
        debounce = setTimeout(onQuery, 200);
      });
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Escape' && input.value) { input.value = ''; onQuery(); }
      });
    }
    if (clearBtn) clearBtn.addEventListener('click', function () { input.value = ''; onQuery(); input.focus(); });

    // ── no-results → hand the query to the in-tool assistant ───────────────
    // The button only appears once the designer bundle announces itself, so it
    // can never be a control that does nothing (e.g. if the bundle is blocked).
    function enableAsk() { if (askBtn) askBtn.hidden = false; }
    if (window.__wdHelpReady) enableAsk();
    window.addEventListener('wd:help-ready', enableAsk);
    if (askBtn) {
      askBtn.addEventListener('click', function () {
        var q = (input && input.value.trim()) || '';
        if (!q) return;
        window.dispatchEvent(new CustomEvent('wd:ask-help', { detail: { question: q } }));
      });
    }

    // ── deep links: #wd-kb-<id> opens the item, ON ITS OWN PAGE ─────────────
    // ⛔ PAGING BROKE THE OLD VERSION SILENTLY. It selected a category and opened
    // the item, which was enough when a category rendered all of its answers at
    // once; now the target can be on page 4, and a shared or assistant-issued
    // link would land on page 1 with the answer "opened" out of sight and
    // nothing visibly wrong. The page has to be computed before the open.
    // It selects the item's OWN category rather than "All" — fewer pages to
    // resolve, and the rail then shows where the answer actually lives.
    function openFromHash() {
      var hash = location.hash.replace(/^#/, '');
      if (!hash || hash.indexOf('wd-kb-') !== 0) return;
      var item = document.getElementById(hash);
      if (!item || !kb.contains(item) || !item.classList.contains('wd-kb__item')) return;
      if (input && input.value) { input.value = ''; currentTokens = []; syncClear(); }
      var panel = item.closest('.wd-kb__category');
      var cat = panel ? panel.getAttribute('data-category') : 'all';
      activeCategory = cat;
      var scope = scopeOf();
      var at = -1;
      for (var i = 0; i < scope.length; i++) { if (scope[i].item === item) { at = i; break; } }
      // Pages are packed by height, so the target's page cannot be divided out
      // of its position — walk the boundaries and find the one that holds it.
      page = 0;
      if (at >= 0) {
        var breaks = pagesOf(scope, isMulti());
        for (var b = 0; b < breaks.length; b++) {
          if (at >= breaks[b][0] && at < breaks[b][0] + breaks[b][1]) { page = b; break; }
        }
      }
      closeAll();
      activate(cat, true);
      toggleItem(item, true);
      requestAnimationFrame(function () { item.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    }
    window.addEventListener('hashchange', openFromHash);

    // ⚠ THE FLOOR IS FONT-DEPENDENT. Measuring before Montserrat has swapped in
    // sizes it against the fallback, and the rows that wrap are not the same set
    // — so the floor comes out wrong and the frame grows on the first page that
    // disagrees. Re-measure once the face is actually in.
    measureFloor();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { measureFloor(); applyFilters(); });
    }
    /* Row heights and the wrap set both change with the column width.
       ⚠ AND SO DOES THE PAGE SIZE, since `perPage()` went per-breakpoint. Re-measuring alone
       updated the frame height but left the pages built at the OLD count, so a phone rotated past
       900px kept five to a page under a desktop-width frame until some other action rebuilt them.
       `applyFilters()` is what re-runs `pagesOf` — the same pairing the fonts-ready path uses. */
    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measureFloor(); applyFilters(); }, 150);
    }
    window.addEventListener('resize', onResize);

    // Held so `shopify:section:unload` can detach them — see the boot block.
    kb._wdKbHashHandler = openFromHash;
    kb._wdKbResizeHandler = onResize;

    activate(DEFAULT_CATEGORY);
    openFromHash();
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('.wd-kb')).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* ⛔ THE THEME EDITOR REPLACES THIS SECTION'S DOM, AND ONE-SHOT BOOT LEAVES IT DEAD (Ryan
     2026-08-13: *"as soon as I dialed up the white below slider, all the questions auto-expanded and
     now I can't close them"*).
       Changing ANY section setting makes Shopify re-render the section through the Section Rendering
     API and swap fresh markup in. `.wd-kb--js` was added to the OLD node and every listener was bound
     to it, so the replacement arrives with neither — and the stylesheet's no-JS state is, by design,
     "answers expanded, tabs and search hidden". So the widget does not merely lose its behaviour, it
     visibly falls open with no way to close it. Nothing to do with the slider that exposed it: this
     fires for a heading edit just the same, and it has been latent since the section was written.
       `init` is already guarded by `data-wd-kb-init` and `boot` walks every root, so re-running it is
     the whole fix — already-live instances are skipped, the new one initialises.
     ⚠ EDITOR-ONLY. A storefront visitor never re-renders a section, which is why this survived.

     `unload` is not symmetry for its own sake: `init` binds `openFromHash` to WINDOW, which outlives
     the section's DOM — and since the paging work, a `resize` handler that re-measures the floor is
     bound there too. Without detaching, every settings change leaves another pair closed over a
     discarded tree, and the resize one runs a measuring pass over detached nodes on every drag. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target;
    if (!root || !root.querySelectorAll) return;
    Array.prototype.slice.call(root.querySelectorAll('.wd-kb')).forEach(function (kb) {
      if (kb._wdKbHashHandler) {
        window.removeEventListener('hashchange', kb._wdKbHashHandler);
        kb._wdKbHashHandler = null;
      }
      if (kb._wdKbResizeHandler) {
        window.removeEventListener('resize', kb._wdKbResizeHandler);
        kb._wdKbResizeHandler = null;
      }
    });
  });
})();
